import { array, enums, number, string, type, size } from "superstruct";
import { MediaAsset, MediaAssetStruct } from "../../../utils/assets";
import { validateStruct } from "../../../utils/validate";
import { ApiEntity} from "../../Api/api.model";

const DurationMapping = {
  week: 1,
  twoweeks: 2,
  threeweeks: 3,
  month: 4
};

type Schedule = "daily" | "weekly" | "biweekly" | "threedaysaweek" | "monthly";

const ScheduleMapping: Record<string | Schedule, number> = {
  daily: 1,
  weekly: 7,
  biweekly: 3,
  threedaysaweek: 2,
  monthly:28
};

type StartTime = "morning" | "noon" | "afternoon" | "diner" | "evening";

const SuggestedStartTime: Record<
  string | StartTime,
  { hour: number; minutes: number }
> = {
  morning: {
    hour: 8,
    minutes: 0,
  },
  noon: {
    hour: 12,
    minutes: 0,
  },
  afternoon: {
    hour: 16,
    minutes: 0,
  },
  diner: {
    hour: 19,
    minutes: 0,
  },
  evening: {
    hour: 19,
    minutes: 0,
  },
};

type Duration = keyof typeof DurationMapping;

type Difficulty = "easy" | "simple" | "average" | "hard";

type Players = "solo" | "group" | "both";

type Category =
  | "food"
  | "nature"
  | "move"
  | "enjoy"
  | "stepback"
  | "purpose"
  | "lifestyle";

interface ChallengeAction {
  actionId?: string;
  order?: number;
}
interface Challenge {
  id: string;
  schedule: Schedule;
  reminder: StartTime;
  category: Category;
  duration: Duration;
  players: Players;
  difficulty: Difficulty;
  coverImg: MediaAsset;
  actions: Array<ChallengeAction>;
  blogs: Array<string>;
  title: string;
  description: string;
  locale:string;
  localizations: Array<{
    id: string;
    locale: string;
  }>;
}

function extractActionsIds(
  actions: Array<{ id: string }>
): Array<{ actionId: string; order: number }> {
  return actions.map((action, index) => ({
    actionId: (action.id as unknown).toString(),
    order: index,
  }));
}

function extractBlogIds(blogs: Array<{ id: string }>): Array<string> {
  return blogs.map((blog) => (blog.id as unknown).toString());
}

const ChallengeStruct = type({
  id: string(),
  schedule: enums([
    "monthly",
    "daily",
    "weekly",
    "biweekly",
    "threedaysaweek",
  ] as Array<Schedule>),
  reminder: enums([
    "afternoon",
    "diner",
    "evening",
    "morning",
    "noon",
  ] as Array<StartTime>),
  category: enums([
    "enjoy",
    "food",
    "lifestyle",
    "move",
    "nature",
    "purpose",
    "stepback",
  ] as Array<Category>),
  duration: enums([
    "justonce",
    "month",
    "threeweeks",
    "twoweeks",
    "week",
  ] as Array<Duration>),
  players: enums(["both", "group", "solo"] as Array<Players>),
  difficulty: enums(["average", "easy", "hard", "simple"] as Array<Difficulty>),
  coverImg: MediaAssetStruct,
  actions: size(
    array(
      type({
        actionId: string(),
        order: number(),
      })
    ),
    1,
    Infinity
  ),
  blogs: array(string()),
  title: string(),
  description: string(),
  locale:string(),
  localizations: size(
    array(
      type({
        id: string(),
        locale: string(),
      })
    ),
    1,
    4
  ),
});
export class ChallengeEntity extends ApiEntity<Array<Challenge>> {
  constructor() {
    super("challenges");
  }
  validate(rawData: Array<any>): Array<Challenge> {
    return rawData.map((rawItem) =>
      validateStruct<Challenge>(rawItem, ChallengeStruct,this.endPoint, [
        { field: "actions", transform: extractActionsIds },
        { field: "blogs", transform: extractBlogIds },
        {
          field: "reminder",
          transform: (value: string) => value.toLowerCase(),
        },
        {
          field: "schedule",
          transform: (value: string) => value.toLowerCase(),
        },
        {
          field: "duration",
          transform: (value: string) => value.toLowerCase(),
        },
        {
          field: "players",
          transform: (value: string) => value.toLowerCase(),
        },
        {
          field: "difficulty",
          transform: (value: string) => value.toLowerCase(),
        },
        { field: "id", transform: (id: number) => id.toString() },
        {
          field: "localizations",
          transform: (localizations: Array<object>) =>
            localizations.map((lc) => ({
              id: lc["id"].toString(),
              locale: lc["locale"],
            })),
        },
      ])
    );
  }
}

export {
  Challenge,
  ChallengeAction,
  DurationMapping,
  ScheduleMapping,
  SuggestedStartTime,
  Schedule,
  Duration,
  Category,
};
